import { ExportOutlined } from "@ant-design/icons";
import { Alert, Button, Modal, Space } from "antd";
import { nanoid } from "nanoid";
import { useState } from "react";
import { AdvancedTextEditor, IEditorInstance } from "../../controls/AdvancedTextEditor";
import { ModelSelector } from "../../controls/ModelSelector";
import { PageTitle } from "../../controls/PageTitle";
import { PageWrapper } from "../../controls/PageWrapper";
import { domain } from "../../domain/domain";
import { camelCase, kebabCase, pascalCase, snakeCase, snakeCaseUpper } from "../../domain/helpers/caseHelpers";
import { jsonCopy } from "../../domain/helpers/tsHelper";
import { IModelInfo } from "../../domain/ModelInfos/IModelInfo";
import { useDatasource } from "../../hooks/useDatasource";

const exampleCode = [
    "const oldModelName = model.name;",
    "model.name = tools.kebabCase(oldModelName);",
    "console.log(oldModelName, '->', model.name);",
    "",
    "for(const member of model.members) {",
    "    const oldMemberName = member.name;",
    "\tmember.name = tools.snakeCase(oldMemberName);",
    // eslint-disable-next-line no-template-curly-in-string
    "\tconsole.log(oldMemberName, '->', member.name, `[type: ${member.type.name}]`);",
    "}"
].join("\n");

export function TransformPage(props: {}) {
    const dsModels = useDatasource(() => domain.modelInfoService.get(), []);
    const [selectedModels, setSelectedModels] = useState<IModelInfo[]>([]);
    const [editor, setEditor] = useState<IEditorInstance>();

    async function runTransform(apply: boolean) {
        if (selectedModels.length < 1) return;
        const code = editor?.getText();
        if (!code) return;
        try {
            const fullFnCode = "return function(model, console, tools) { var window, document, alert, confirm, self, parent, global; try { " + code + " } catch(e) { console.error(e); }};"
            // eslint-disable-next-line no-new-func
            const fn = new Function(fullFnCode);
            const fn2 = fn();
            const tools = {
                camelCase,
                kebabCase,
                pascalCase,
                snakeCase,
                snakeCaseUpper,
            };
            const transformedModels: IModelInfo[] = [];
            for (const model of selectedModels) {
                const copy: IModelInfo = jsonCopy(model);
                fn2(copy, console, tools);
                copy.id = copy.id ?? nanoid();
                copy.rev = nanoid();
                transformedModels.push(copy);
            }

            if (apply) {
                await domain.modelInfoService.save(transformedModels);
                dsModels.updateData();
                Modal.success({
                    content: `Successfully transformed ${transformedModels.length} model(s)`
                })
            }
        } catch (err) {
            console.error(err);
        }
    }

    const messageContent = <>
        First select models above. <br />
        Please use DevTools console to inspect the API and debug code changes.<br />
        Currently available in tools: camelCase, kebabCase, pascalCase, snakeCase, snakeCaseUpper<br />
    </>;

    return (<PageWrapper>
        <PageTitle title="Export" />
        <div style={{ padding: 24, minHeight: 360, backgroundColor: "#fff" }}>
            <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
                <ModelSelector models={dsModels.data} onChange={setSelectedModels} />
                <AdvancedTextEditor mode="javascript" initialValue={exampleCode} width="100%" height="300px" onInstance={setEditor} />
                <Alert message={messageContent} type="info" showIcon />
                <Space>
                    <Button type="primary" icon={<ExportOutlined />} onClick={() => runTransform(false)}>Test Transform</Button>
                    <Button type="primary" icon={<ExportOutlined />} onClick={() => runTransform(true)}>Apply Transform</Button>
                </Space>
            </Space>
        </div>
    </PageWrapper>
    );
};